import { History, Sparkles, AlertTriangle, Loader2 } from "lucide-react";

/**
 * Renders the "from last shift" handoff card on the caregiver shift report
 * page. Shows the AI-generated 2-sentence brief + the previous caregiver's
 * raw note. Three render states:
 *   1. handoffLoading -> spinner
 *   2. handoff.previous -> full card
 *   3. else -> "first documented shift" empty state
 */
export default function HandoffBriefingCard({
  handoffLoading,
  handoff,
  showHandoff,
  onToggle,
}) {
  if (handoffLoading) {
    return (
      <div className="card-surface p-4 flex items-center gap-2 text-sm text-clinical-subtext">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading last shift…
      </div>
    );
  }
  if (!handoff?.previous) {
    return (
      <div className="rounded-2xl border border-dashed border-clinical-border p-4 text-center text-sm text-clinical-subtext">
        <History className="h-4 w-4 mx-auto mb-1" />
        First documented shift for this client.
      </div>
    );
  }
  return (
    <div
      className="rounded-2xl border border-clinical-accent/30 bg-clinical-accent/5 p-4"
      data-testid="handoff-card"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <History className="h-4 w-4 text-clinical-accent" />
          <span className="label-eyebrow !text-clinical-accent">From last shift</span>
        </div>
        <button
          onClick={onToggle}
          className="text-xs text-clinical-accent hover:underline"
          data-testid="toggle-handoff"
        >
          {showHandoff ? "Hide" : "Show"}
        </button>
      </div>

      {showHandoff && (
        <>
          {handoff.brief && (
            <div className="mb-3 rounded-lg bg-white/70 border border-clinical-accent/20 px-3 py-2.5">
              <div className="text-[11px] uppercase tracking-wide text-clinical-accent font-semibold mb-1 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> AI briefing
              </div>
              <p className="text-sm leading-relaxed text-clinical-text">
                {handoff.brief}
              </p>
            </div>
          )}

          <div className="text-xs text-clinical-subtext mb-1.5">
            {handoff.previous.shift_date} ·{" "}
            <span className="capitalize">{handoff.previous.shift_type}</span> · by{" "}
            {handoff.previous.caregiver_first_name}
            {handoff.previous.incident_flag && (
              <span className="ml-1.5 pill bg-destructive/10 text-destructive">
                <AlertTriangle className="h-3 w-3" /> Incident
              </span>
            )}
          </div>
          <p className="text-sm whitespace-pre-wrap leading-relaxed text-clinical-text">
            {handoff.previous.formatted_notes}
          </p>
        </>
      )}
    </div>
  );
}
